/**
 * Pachisi Cowrie Shells Component
 *
 * Renders 6 cowrie shells with throwing animation.
 * Some shells face-up (showing opening) and some face-down (smooth).
 * Click/touch to trigger throw; disabled when not player's turn.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { PlayerColor, NUM_COWRIE_SHELLS, COWRIE_VALUES, GRACE_THROWS } from '../types';
import './PachisiCowries.css';

interface PachisiCowriesProps {
  shellsUp: number | null;
  moveValue: number | null;
  throwing: boolean;
  onThrow: () => void;
  disabled: boolean;
  playerColor: PlayerColor;
}

const COLOR_BORDERS: Record<PlayerColor, string> = {
  red: 'var(--pachisi-red, #c62828)',
  blue: 'var(--pachisi-blue, #1565c0)',
  green: 'var(--pachisi-green, #2e7d32)',
  yellow: 'var(--pachisi-yellow, #f9a825)',
};

const PachisiCowries: React.FC<PachisiCowriesProps> = ({
  shellsUp,
  moveValue,
  throwing,
  onThrow,
  disabled,
  playerColor,
}) => {
  const [displayShellsUp, setDisplayShellsUp] = useState<number>(shellsUp ?? 3);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (!throwing) {
      if (shellsUp !== null) setDisplayShellsUp(shellsUp);
      setIsAnimating(false);
      return;
    }

    setIsAnimating(true);
    let frame = 0;
    const totalFrames = 12;
    const interval = setInterval(() => {
      setDisplayShellsUp(Math.floor(Math.random() * 7)); // 0-6
      frame++;
      if (frame >= totalFrames) {
        clearInterval(interval);
        if (shellsUp !== null) setDisplayShellsUp(shellsUp);
        setIsAnimating(false);
      }
    }, 70);

    return () => clearInterval(interval);
  }, [throwing, shellsUp]);

  const handleClick = useCallback(() => {
    if (!disabled && !throwing) {
      onThrow();
    }
  }, [disabled, throwing, onThrow]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if ((e.key === 'Enter' || e.key === ' ') && !disabled && !throwing) {
        e.preventDefault();
        onThrow();
      }
    },
    [disabled, throwing, onThrow]
  );

  const isGrace = shellsUp !== null && GRACE_THROWS.includes(shellsUp);
  const displayMoveValue = shellsUp !== null ? COWRIE_VALUES[shellsUp] : null;

  // Render individual cowrie shell
  const renderShell = (index: number, faceUp: boolean) => (
    <div
      key={index}
      className={`pachisi-cowries__shell ${faceUp ? 'pachisi-cowries__shell--up' : 'pachisi-cowries__shell--down'} ${isAnimating ? 'pachisi-cowries__shell--animating' : ''}`}
      aria-hidden="true"
    >
      <svg viewBox="0 0 30 20" className="pachisi-cowries__shell-svg">
        {faceUp ? (
          // Face-up: shell with opening (slit)
          <>
            <ellipse cx="15" cy="10" rx="13" ry="9" fill="#f5e6d0" stroke="#8d6e4a" strokeWidth="1" />
            <ellipse cx="15" cy="10" rx="13" ry="9" fill="none" stroke="#c4a67a" strokeWidth="0.5" />
            <line x1="8" y1="10" x2="22" y2="10" stroke="#6d4c2a" strokeWidth="1.5" strokeLinecap="round" />
            <line x1="9" y1="7" x2="21" y2="7" stroke="#8d6e4a" strokeWidth="0.5" strokeLinecap="round" opacity="0.5" />
            <line x1="9" y1="13" x2="21" y2="13" stroke="#8d6e4a" strokeWidth="0.5" strokeLinecap="round" opacity="0.5" />
          </>
        ) : (
          // Face-down: smooth rounded shell
          <>
            <ellipse cx="15" cy="10" rx="13" ry="9" fill="#e8d5b7" stroke="#8d6e4a" strokeWidth="1" />
            <ellipse cx="13" cy="8" rx="6" ry="4" fill="rgba(255,255,255,0.2)" />
          </>
        )}
      </svg>
    </div>
  );

  return (
    <div className="pachisi-cowries-container">
      <button
        className={`pachisi-cowries ${isAnimating ? 'pachisi-cowries--throwing' : ''} ${disabled ? 'pachisi-cowries--disabled' : ''}`}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-label={`Cowrie shells${shellsUp !== null ? `: ${shellsUp} face up, move ${displayMoveValue}` : ''}. ${disabled ? 'Not your turn.' : 'Click to throw.'}`}
        aria-live="polite"
        role="button"
        tabIndex={0}
        style={{ borderColor: COLOR_BORDERS[playerColor] }}
      >
        <div className="pachisi-cowries__shells">
          {Array.from({ length: NUM_COWRIE_SHELLS }, (_, i) =>
            renderShell(i, i < displayShellsUp)
          )}
        </div>
      </button>

      {/* Result display */}
      {shellsUp !== null && !throwing && (
        <div className={`pachisi-cowries__result ${isGrace ? 'pachisi-cowries__result--grace' : ''}`}>
          <span className="pachisi-cowries__result-shells">
            {shellsUp} shell{shellsUp !== 1 ? 's' : ''} up
          </span>
          <span className="pachisi-cowries__result-value">
            Move: {displayMoveValue}
          </span>
          {isGrace && (
            <span className="pachisi-cowries__result-grace">
              🐚 Grace throw!
            </span>
          )}
        </div>
      )}

      {!disabled && !throwing && (
        <p className="pachisi-cowries__hint" aria-hidden="true">
          Tap to throw cowries
        </p>
      )}
      {throwing && (
        <p className="pachisi-cowries__hint pachisi-cowries__hint--throwing" aria-live="assertive">
          Throwing...
        </p>
      )}
    </div>
  );
};

export { PachisiCowries };
export default PachisiCowries;
